import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { DateRange } from '../models/CustomerStats';

export type SalesScopeAggregate = {
  scopeType: 'store' | 'group' | 'global';
  scopeId: string;
};

export type SalesScopeTimelinePoint = {
  key: string;
  label: string;
  revenue: number;
  orders: number;
};

export type SalesScopeDailySummary = {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  deliveryOrders: number;
  pickupOrders: number;
  deliveryRevenue: number;
  totalDeliveryFees: number;
  totalDriverCommission: number;
  netDeliveryRevenue: number;
  pickupRevenue: number;
  cardOrders: number;
  cashOrders: number;
  cardRevenue: number;
  cashRevenue: number;
  deliveredOrders: number;
  cancelledOrders: number;
  activeOrders: number;
  promoUses: number;
  promoAmountTotal: number;
  uniquePromoCodes: number;
  uniqueCustomers: number;
  timeline: SalesScopeTimelinePoint[];
  coveredDayKeys: string[];
};

type SalesScopeDailyDoc = {
  dayKey?: string;
  totalOrders?: number;
  totalRevenue?: number;
  deliveryOrders?: number;
  pickupOrders?: number;
  deliveryRevenue?: number;
  totalDeliveryFees?: number;
  totalDriverCommission?: number;
  netDeliveryRevenue?: number;
  pickupRevenue?: number;
  cardOrders?: number;
  cashOrders?: number;
  cardRevenue?: number;
  cashRevenue?: number;
  deliveredOrders?: number;
  cancelledOrders?: number;
  activeOrders?: number;
  promoUses?: number;
  promoAmountTotal?: number;
  uniquePromoCodes?: number;
  uniqueCustomers?: number;
};

class SalesScopeDailyService {
  private readonly collectionName = 'sales_scope_daily';

  private zeroSummary(dateRange: DateRange | undefined): SalesScopeDailySummary {
    return {
      totalOrders: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
      deliveryOrders: 0,
      pickupOrders: 0,
      deliveryRevenue: 0,
      totalDeliveryFees: 0,
      totalDriverCommission: 0,
      netDeliveryRevenue: 0,
      pickupRevenue: 0,
      cardOrders: 0,
      cashOrders: 0,
      cardRevenue: 0,
      cashRevenue: 0,
      deliveredOrders: 0,
      cancelledOrders: 0,
      activeOrders: 0,
      promoUses: 0,
      promoAmountTotal: 0,
      uniquePromoCodes: 0,
      uniqueCustomers: 0,
      timeline: this.buildEmptyTimeline(dateRange),
      coveredDayKeys: [],
    };
  }

  private normalizeNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private buildDayKey(date: Date): string {
    const localDay = new Date(date);
    localDay.setHours(0, 0, 0, 0);
    return localDay.toISOString().slice(0, 10);
  }

  private buildEmptyTimeline(dateRange: DateRange | undefined): SalesScopeTimelinePoint[] {
    if (!dateRange) {
      return [];
    }

    const buckets: SalesScopeTimelinePoint[] = [];
    const cursor = new Date(dateRange.start);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(dateRange.end);
    end.setHours(0, 0, 0, 0);

    while (cursor <= end) {
      buckets.push({
        key: cursor.toISOString().slice(0, 10),
        label: cursor.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        revenue: 0,
        orders: 0,
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return buckets;
  }

  async getSummary(
    scope: SalesScopeAggregate,
    dateRange: DateRange | undefined
  ): Promise<SalesScopeDailySummary> {
    if (!dateRange) {
      return this.zeroSummary(dateRange);
    }

    const startDayKey = this.buildDayKey(dateRange.start);
    const endDayKey = this.buildDayKey(dateRange.end);

    const snap = await getDocs(
      query(
        collection(db, this.collectionName),
        where('scopeType', '==', scope.scopeType),
        where('scopeId', '==', scope.scopeId),
        where('dayKey', '>=', startDayKey),
        where('dayKey', '<=', endDayKey),
        orderBy('dayKey', 'asc')
      )
    );

    const summary = this.zeroSummary(dateRange);
    const timeline = this.buildEmptyTimeline(dateRange);
    const timelineByKey = new Map(timeline.map((point) => [point.key, point] as const));
    const coveredDayKeys = new Set<string>();

    snap.forEach((docSnap) => {
      const data = docSnap.data() as SalesScopeDailyDoc;
      const dayKey = typeof data.dayKey === 'string' ? data.dayKey : '';
      if (!dayKey) {
        return;
      }

      coveredDayKeys.add(dayKey);

      summary.totalOrders += this.normalizeNumber(data.totalOrders);
      summary.totalRevenue += this.normalizeNumber(data.totalRevenue);
      summary.deliveryOrders += this.normalizeNumber(data.deliveryOrders);
      summary.pickupOrders += this.normalizeNumber(data.pickupOrders);
      summary.deliveryRevenue += this.normalizeNumber(data.deliveryRevenue);
      summary.totalDeliveryFees += this.normalizeNumber(data.totalDeliveryFees);
      summary.totalDriverCommission += this.normalizeNumber(data.totalDriverCommission);
      summary.netDeliveryRevenue += this.normalizeNumber(data.netDeliveryRevenue);
      summary.pickupRevenue += this.normalizeNumber(data.pickupRevenue);
      summary.cardOrders += this.normalizeNumber(data.cardOrders);
      summary.cashOrders += this.normalizeNumber(data.cashOrders);
      summary.cardRevenue += this.normalizeNumber(data.cardRevenue);
      summary.cashRevenue += this.normalizeNumber(data.cashRevenue);
      summary.deliveredOrders += this.normalizeNumber(data.deliveredOrders);
      summary.cancelledOrders += this.normalizeNumber(data.cancelledOrders);
      summary.activeOrders += this.normalizeNumber(data.activeOrders);
      summary.promoUses += this.normalizeNumber(data.promoUses);
      summary.promoAmountTotal += this.normalizeNumber(data.promoAmountTotal);
      summary.uniquePromoCodes += this.normalizeNumber(data.uniquePromoCodes);
      summary.uniqueCustomers += this.normalizeNumber(data.uniqueCustomers);

      const point = timelineByKey.get(dayKey);
      if (point) {
        point.revenue = this.normalizeNumber(data.totalRevenue);
        point.orders = this.normalizeNumber(data.totalOrders);
      }
    });

    summary.averageOrderValue = summary.totalOrders > 0
      ? summary.totalRevenue / summary.totalOrders
      : 0;
    summary.timeline = timeline;
    summary.coveredDayKeys = Array.from(coveredDayKeys.values()).sort();

    return summary;
  }
}

const salesScopeDailyService = new SalesScopeDailyService();
export default salesScopeDailyService;
